import React, { useState, useEffect, useContext } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  makeStyles,
  useTheme,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Menu,
  InputAdornment,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from "@material-ui/core";
import {
  Brush,
  CloudUpload,
  Visibility,
  VisibilityOff,
  Delete,
  VpnKey,
  Memory,
  Tune,
  Info
} from "@material-ui/icons";
import { toast } from "react-toastify";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(1, 0),
  },
  card: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: theme.palette.type === "dark" ? "0 4px 20px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.05)",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "rgba(0, 229, 255, 0.25)",
      boxShadow: "0 8px 32px 0 rgba(0, 229, 255, 0.08)",
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
    paddingBottom: theme.spacing(1.5),
  },
  sectionTitle: {
    fontSize: "0.95rem",
    fontWeight: 700,
    letterSpacing: "0.5px",
    color: theme.palette.primary.main,
    textTransform: "uppercase",
  },
  sectionIcon: {
    color: theme.palette.primary.main,
    fontSize: "1.4rem",
  },
  infoBanner: {
    background: `${theme.palette.primary.main}0A`,
    border: `1px solid ${theme.palette.primary.main}33`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
  },
  infoBannerIcon: {
    color: theme.palette.primary.main,
    fontSize: "1.3rem",
    marginTop: 2,
    flexShrink: 0,
  },
  infoBannerText: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  actionButton: {
    borderRadius: 8,
    fontWeight: 700,
    fontSize: "0.88rem",
    textTransform: "none",
    padding: "8px 26px",
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: `${theme.palette.primary.contrastText || "#ffffff"} !important`,
    boxShadow: `0 0 14px ${theme.palette.primary.main}66`,
    transition: "all 0.25s ease",
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}CC !important`,
      boxShadow: `0 0 18px ${theme.palette.primary.main}99`,
    },
    "&:disabled": {
      backgroundColor: "rgba(255, 255, 255, 0.12) !important",
      color: "rgba(255, 255, 255, 0.3) !important",
      boxShadow: "none",
    },
  },
  outlineButton: {
    borderColor: `${theme.palette.primary.main}80`,
    color: theme.palette.primary.main,
    fontWeight: 600,
    borderRadius: 8,
    textTransform: "none",
    padding: "6px 16px",
    transition: "all 0.25s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}14`,
      boxShadow: `0 0 10px ${theme.palette.primary.main}33`,
    },
  },
  dialogPaper: {
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
    border: theme.palette.type === "dark" ? `1px solid ${theme.palette.primary.main}40` : "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 14,
    color: "#fff",
    boxShadow: "0 20px 50px rgba(0, 0, 0, 0.8)",
  },
  dialogTitle: {
    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
    padding: theme.spacing(2, 3),
    "& h2": {
      color: theme.palette.primary.main,
      fontWeight: 700,
      fontSize: "1.15rem",
    },
  },
  tableCellHead: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
    backgroundColor: `${theme.palette.primary.main}0A`,
  },
  tableCellBody: {
    borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
    color: theme.palette.text.primary,
  },
  menuPaper: {
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
    border: theme.palette.type === "dark" ? "1px solid rgba(0, 229, 255, 0.2)" : "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 8,
    color: "#fff",
  },
}));

const getPromptTemplates = () => [
  {
    label: i18n.t("settings.brandKitIA.templates.saas.label"),
    value: i18n.t("settings.brandKitIA.templates.saas.value"),
  },
  {
    label: i18n.t("settings.brandKitIA.templates.ecommerce.label"),
    value: i18n.t("settings.brandKitIA.templates.ecommerce.value"),
  },
  {
    label: i18n.t("settings.brandKitIA.templates.clinic.label"),
    value: i18n.t("settings.brandKitIA.templates.clinic.value"),
  },
  {
    label: i18n.t("settings.brandKitIA.templates.food.label"),
    value: i18n.t("settings.brandKitIA.templates.food.value"),
  },
];

const BrandKitIA = ({ settings, onSettingsChange }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { update } = useCompanySettings();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [previewLogo, setPreviewLogo] = useState(null);
  const [dbModels, setDbModels] = useState([]);
  const [pricesModalOpen, setPricesModalOpen] = useState(false);
  const [showOpenAiKey, setShowOpenAiKey] = useState(false);
  const [showKieKey, setShowKieKey] = useState(false);

  const [form, setForm] = useState({
    aiBrandKit: "",
    aiModel: "gpt-image-2-text-to-image",
    aiModelText: "gpt-4o-mini",
    aiModelAudio: "whisper-1",
    aiModelVideo: "",
    aiModelMusic: "",
    kieApiKey: "",
    openaiApiKey: "",
    aiRequireApproval: true,
    aiLogo: "",
    aiUseLogo: false,
  });

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const { data } = await api.get("/ai-models");
        const activeModels = data.filter((m) => m.active);
        setDbModels(activeModels);
      } catch (err) {
        console.error("Error fetching AI models", err);
      }
    };
    fetchModels();
  }, []);

  useEffect(() => {
    if (settings) {
      setForm({
        aiBrandKit: settings.aiBrandKit || "",
        aiModel: settings.aiModel || "gpt-image-2-text-to-image",
        aiModelText: settings.aiModelText || "gpt-4o-mini",
        aiModelAudio: settings.aiModelAudio || "whisper-1",
        aiModelVideo: settings.aiModelVideo || "",
        aiModelMusic: settings.aiModelMusic || "",
        kieApiKey: settings.kieApiKey || "",
        openaiApiKey: settings.openaiApiKey || "",
        aiRequireApproval:
          settings.aiRequireApproval !== undefined
            ? settings.aiRequireApproval
            : true,
        aiLogo: settings.aiLogo || "",
        aiUseLogo:
          settings.aiUseLogo !== undefined ? settings.aiUseLogo : false,
      });
      if (settings.aiLogo) {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
        setPreviewLogo(`${backendUrl}/public/${settings.aiLogo}`);
      }
    }
  }, [settings]);

  const handleChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const applyTemplate = (tpl) => {
    setForm((prev) => ({ ...prev, aiBrandKit: tpl.value }));
    setAnchorEl(null);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setPreviewLogo(URL.createObjectURL(file));
    }
  };

  const handleDeleteLogo = async () => {
    setLogoFile(null);
    setPreviewLogo(null);
    setForm((prev) => ({ ...prev, aiLogo: "", aiUseLogo: false }));
    await update({ column: "aiLogo", data: "" });
    await update({ column: "aiUseLogo", data: false });
    toast.success(
      i18n.t("settings.brandKitIA.logoDeleteSuccess", "Logo eliminado correctamente")
    );
  };

  const saveField = async (field) => {
    await update({ column: field, data: form[field] });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (logoFile) {
        const formData = new FormData();
        formData.append("file", logoFile);
        const { data: updatedSettings } = await api.post(
          `/companySettings/${settings.companyId}/ai-logo`,
          formData
        );
        setForm((prev) => ({ ...prev, aiLogo: updatedSettings.aiLogo }));
      }

      await Promise.all([
        saveField("aiBrandKit"),
        saveField("aiModel"),
        saveField("aiModelText"),
        saveField("aiModelAudio"),
        saveField("aiModelVideo"),
        saveField("aiModelMusic"),
        saveField("kieApiKey"),
        saveField("openaiApiKey"),
        update({ column: "aiRequireApproval", data: form.aiRequireApproval }),
        update({ column: "aiUseLogo", data: form.aiUseLogo }),
      ]);
      toast.success(i18n.t("settings.brandKitIA.success"));
      if (onSettingsChange) onSettingsChange({ ...settings, ...form });
    } catch (e) {
      toast.error(i18n.t("settings.brandKitIA.error"));
    }
    setLoading(false);
  };

  return (
    <div className={classes.root} dir={i18n.language === "ar" ? "rtl" : "ltr"}>
      {/* 1. CREDENCIALES Y CONECTIVIDAD IA (Solo Superadmin) */}
      {user?.super && (
        <Paper className={classes.card} elevation={0}>
          <div className={classes.sectionHeader}>
            <VpnKey className={classes.sectionIcon} />
            <Typography className={classes.sectionTitle}>
              {i18n.t(
                "settings.brandKitIA.sections.credentials",
                "Credenciales y Conectividad IA"
              )}
            </Typography>
          </div>

          <div className={classes.infoBanner}>
            <Info className={classes.infoBannerIcon} />
            <Typography
              className={classes.infoBannerText}
              dangerouslySetInnerHTML={{
                __html: i18n.t("settings.brandKitIA.tip"),
              }}
            />
          </div>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type={showOpenAiKey ? "text" : "password"}
                autoComplete="new-password"
                label={
                  <span>
                    OpenAI API Key (ChatGPT){" "}
                    <strong style={{ color: "#f44336" }}>*</strong>
                  </span>
                }
                value={form.openaiApiKey}
                onChange={handleChange("openaiApiKey")}
                variant="outlined"
                helperText={i18n.t("settings.brandKitIA.openAiHelper")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowOpenAiKey(!showOpenAiKey)}
                        style={{ color: theme.palette.primary.main }}
                      >
                        {showOpenAiKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type={showKieKey ? "text" : "password"}
                autoComplete="new-password"
                label={
                  <span>
                    {i18n.t("settings.brandKitIA.kieApiKey")}{" "}
                    <strong style={{ color: "#f44336" }}>*</strong>
                  </span>
                }
                value={form.kieApiKey}
                onChange={handleChange("kieApiKey")}
                variant="outlined"
                helperText={i18n.t("settings.brandKitIA.kieApiKeyHelper")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowKieKey(!showKieKey)}
                        style={{ color: theme.palette.primary.main }}
                      >
                        {showKieKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* 2. CENTRAL DE MODELOS DE INTELIGENCIA ARTIFICIAL (Solo Superadmin) */}
      {user?.super && (
        <Paper className={classes.card} elevation={0}>
          <div className={classes.sectionHeader}>
            <Memory className={classes.sectionIcon} />
            <Typography className={classes.sectionTitle}>
              {i18n.t(
                "settings.brandKitIA.sections.models",
                "Central de Modelos de Inteligencia Artificial"
              )}
            </Typography>
          </div>

          <div className={classes.infoBanner}>
            <Info className={classes.infoBannerIcon} />
            <Typography className={classes.infoBannerText}>
              <strong>{i18n.t("settings.brandKitIA.aiCenterTitle")}:</strong>{" "}
              {i18n.t("settings.brandKitIA.aiCenterDesc")}
            </Typography>
          </div>

          <Grid container spacing={2}>
            {/* Modelo Texto / Chat */}
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth size="small">
                <InputLabel id="ai-model-text-label">
                  {i18n.t("settings.brandKitIA.aiModelTextLabel")}
                </InputLabel>
                <Select
                  labelId="ai-model-text-label"
                  value={form.aiModelText}
                  onChange={handleChange("aiModelText")}
                  label={i18n.t("settings.brandKitIA.aiModelTextLabel")}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 380, minWidth: 320 },
                      className: classes.menuPaper,
                    },
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ color: theme.palette.primary.main, fontWeight: "bold" }}
                  >
                    <em>{i18n.t("settings.brandKitIA.aiModelTextDefault")}</em>
                  </MenuItem>
                  {dbModels
                    .filter((m) => m.type === "Text Generation")
                    .map((model) => (
                      <MenuItem
                        key={model.apiIdentifier}
                        value={model.apiIdentifier}
                      >
                        {model.name} (${model.credits})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Modelo Transcripción de Audio */}
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth size="small">
                <InputLabel id="ai-model-audio-label">
                  {i18n.t("settings.brandKitIA.aiModelAudioLabel")}
                </InputLabel>
                <Select
                  labelId="ai-model-audio-label"
                  value={form.aiModelAudio}
                  onChange={handleChange("aiModelAudio")}
                  label={i18n.t("settings.brandKitIA.aiModelAudioLabel")}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 380, minWidth: 320 },
                      className: classes.menuPaper,
                    },
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ color: theme.palette.primary.main, fontWeight: "bold" }}
                  >
                    <em>{i18n.t("settings.brandKitIA.aiModelAudioDefault")}</em>
                  </MenuItem>
                  {dbModels
                    .filter(
                      (m) =>
                        m.type === "Audio Transcription" || m.type === "Audio"
                    )
                    .map((model) => (
                      <MenuItem
                        key={model.apiIdentifier}
                        value={model.apiIdentifier}
                      >
                        {model.name} (${model.credits})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Modelo Generación de Imágenes */}
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth size="small">
                <InputLabel id="ai-model-image-label">
                  {i18n.t("settings.brandKitIA.aiModelImageLabel")}
                </InputLabel>
                <Select
                  labelId="ai-model-image-label"
                  value={form.aiModel}
                  onChange={handleChange("aiModel")}
                  label={i18n.t("settings.brandKitIA.aiModelImageLabel")}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 380, minWidth: 400 },
                      className: classes.menuPaper,
                    },
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ color: theme.palette.primary.main, fontWeight: "bold" }}
                  >
                    <em>{i18n.t("settings.brandKitIA.aiModelImageDefault")}</em>
                  </MenuItem>
                  {dbModels
                    .filter(
                      (m) =>
                        m.type === "Text to Image" ||
                        m.type === "Image to Image"
                    )
                    .map((model) => (
                      <MenuItem
                        key={model.apiIdentifier}
                        value={model.apiIdentifier}
                      >
                        {model.name} ({model.type}) - ${model.credits}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Modelo Generación de Video */}
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth size="small">
                <InputLabel id="ai-model-video-label">
                  {i18n.t("settings.brandKitIA.aiModelVideoLabel")}
                </InputLabel>
                <Select
                  labelId="ai-model-video-label"
                  value={form.aiModelVideo}
                  onChange={handleChange("aiModelVideo")}
                  label={i18n.t("settings.brandKitIA.aiModelVideoLabel")}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 380, minWidth: 400 },
                      className: classes.menuPaper,
                    },
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ color: theme.palette.primary.main, fontWeight: "bold" }}
                  >
                    <em>{i18n.t("settings.brandKitIA.aiModelVideoDefault")}</em>
                  </MenuItem>
                  {dbModels
                    .filter(
                      (m) =>
                        m.type === "Text to Video" ||
                        m.type === "Image to Video" ||
                        m.type === "Video to Video"
                    )
                    .map((model) => (
                      <MenuItem
                        key={model.apiIdentifier}
                        value={model.apiIdentifier}
                      >
                        {model.name} ({model.type}) - ${model.credits}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Modelo de Voz (TTS) */}
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth size="small">
                <InputLabel id="ai-model-tts-label">
                  {i18n.t(
                    "settings.brandKitIA.aiModelVoiceLabel",
                    "Modelo de Voz (TTS)"
                  )}
                </InputLabel>
                <Select
                  labelId="ai-model-tts-label"
                  value={form.aiModelAudio}
                  onChange={handleChange("aiModelAudio")}
                  label={i18n.t(
                    "settings.brandKitIA.aiModelVoiceLabel",
                    "Modelo de Voz (TTS)"
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 380, minWidth: 320 },
                      className: classes.menuPaper,
                    },
                  }}
                >
                  <MenuItem value="whisper-1">Whisper (OpenAI)</MenuItem>
                  {dbModels
                    .filter(
                      (m) => m.type === "Text to Audio" || m.type === "TTS"
                    )
                    .map((model) => (
                      <MenuItem
                        key={model.apiIdentifier}
                        value={model.apiIdentifier}
                      >
                        {model.name} ({model.type})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Modelo de Música (Suno) */}
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth size="small">
                <InputLabel id="ai-model-music-label">
                  {i18n.t(
                    "settings.brandKitIA.aiModelMusicLabel",
                    "Modelo de Música (Suno)"
                  )}
                </InputLabel>
                <Select
                  labelId="ai-model-music-label"
                  value={form.aiModelMusic}
                  onChange={handleChange("aiModelMusic")}
                  label={i18n.t(
                    "settings.brandKitIA.aiModelMusicLabel",
                    "Modelo de Música (Suno)"
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 380, minWidth: 320 },
                      className: classes.menuPaper,
                    },
                  }}
                >
                  <MenuItem value="">
                    {i18n.t(
                      "settings.brandKitIA.musicNoneOption",
                      "Ninguno (Librería Gratuita)"
                    )}
                  </MenuItem>
                  <MenuItem value="suno-v3">Suno v3 API</MenuItem>
                  {dbModels
                    .filter((m) => m.type === "Music")
                    .map((model) => (
                      <MenuItem
                        key={model.apiIdentifier}
                        value={model.apiIdentifier}
                      >
                        {model.name} ({model.type})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Botón Catálogo de Precios */}
            <Grid item xs={12} style={{ marginTop: 8 }}>
              <Button
                variant="outlined"
                className={classes.outlineButton}
                onClick={() => setPricesModalOpen(true)}
              >
                {i18n.t("settings.brandKitIA.viewCatalog")}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* 3. IDENTIDAD VISUAL Y MARCA DE AGUA (Para todas las empresas) */}
      <Paper className={classes.card} elevation={0}>
        <div className={classes.sectionHeader}>
          <Brush className={classes.sectionIcon} />
          <Typography className={classes.sectionTitle}>
            {i18n.t(
              "settings.brandKitIA.sections.branding",
              "Identidad Visual y Marca de Agua"
            )}
          </Typography>
        </div>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <input
                accept="image/png"
                style={{ display: "none" }}
                id="logo-upload"
                type="file"
                onChange={handleLogoChange}
              />
              <label htmlFor="logo-upload">
                <Button
                  variant="outlined"
                  className={classes.outlineButton}
                  component="span"
                  startIcon={<CloudUpload />}
                >
                  {i18n.t("settings.brandKitIA.logoWatermark")}
                </Button>
              </label>

              {previewLogo && (
                <div style={{ position: "relative", display: "inline-block" }}>
                  <Avatar
                    src={previewLogo}
                    variant="rounded"
                    style={{
                      width: 54,
                      height: 54,
                      border: "1px solid rgba(0, 229, 255, 0.4)",
                      background: "rgba(0, 0, 0, 0.5)",
                      objectFit: "contain",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleDeleteLogo}
                    style={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      backgroundColor: "#f44336",
                      color: "#fff",
                      padding: 2,
                      boxShadow: "0 0 6px rgba(0,0,0,0.4)",
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </div>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.aiUseLogo}
                    onChange={handleChange("aiUseLogo")}
                    color="primary"
                    disabled={!previewLogo}
                  />
                }
                label={i18n.t("settings.brandKitIA.logoWatermarkCheck")}
              />
            </div>
            <Typography
              variant="caption"
              display="block"
              style={{ color: "rgba(255, 255, 255, 0.6)", marginTop: 4 }}
            >
              {i18n.t("settings.brandKitIA.logoWatermarkDesc")}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* 4. ESTILO VISUAL BASE Y PROMPT BASE */}
      <Paper className={classes.card} elevation={0}>
        <div className={classes.sectionHeader}>
          <Tune className={classes.sectionIcon} />
          <Typography className={classes.sectionTitle}>
            {i18n.t(
              "settings.brandKitIA.sections.promptBase",
              "Estilo Visual Base y Prompt Base"
            )}
          </Typography>
        </div>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <Typography
                variant="body2"
                style={{ color: "rgba(255, 255, 255, 0.7)" }}
              >
                {user?.super
                  ? i18n.t("settings.brandKitIA.subtitle")
                  : i18n.t(
                      "settings.brandKitIA.subtitleCompany",
                      "Configura el estilo visual base (Prompt Base) de tu empresa. Este texto se combinará automáticamente al generar imágenes con IA."
                    )}
              </Typography>

              <Button
                size="small"
                variant="outlined"
                className={classes.outlineButton}
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                {i18n.t("settings.brandKitIA.loadTemplate")}
              </Button>
              <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                PaperProps={{ className: classes.menuPaper }}
              >
                {getPromptTemplates().map((template, index) => (
                  <MenuItem key={index} onClick={() => applyTemplate(template)}>
                    {template.label}
                  </MenuItem>
                ))}
              </Menu>
            </div>

            <TextField
              fullWidth
              multiline
              minRows={6}
              label={i18n.t("settings.brandKitIA.promptBase")}
              value={form.aiBrandKit}
              onChange={handleChange("aiBrandKit")}
              variant="outlined"
              placeholder={i18n.t("settings.brandKitIA.promptPlaceholder")}
              helperText={i18n.t("settings.brandKitIA.promptStrict")}
              inputProps={{
                style: {
                  textAlign: "justify",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  lineHeight: 1.6,
                },
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* BOTÓN GUARDAR CONFIGURACIÓN */}
      <div className={classes.actions}>
        <Button
          variant="contained"
          className={classes.actionButton}
          startIcon={
            loading ? <CircularProgress size={18} color="inherit" /> : null
          }
          onClick={handleSave}
          disabled={loading}
        >
          {loading
            ? i18n.t("settings.brandKitIA.saving")
            : i18n.t("settings.brandKitIA.save")}
        </Button>
      </div>

      {/* MODAL CATÁLOGO DE PRECIOS */}
      <Dialog
        open={pricesModalOpen}
        onClose={() => setPricesModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          {i18n.t("settings.brandKitIA.catalog.title")}
        </DialogTitle>
        <DialogContent dividers style={{ borderColor: "rgba(255, 255, 255, 0.08)" }}>
          <Typography
            variant="body2"
            style={{ color: "rgba(255, 255, 255, 0.7)", marginBottom: 16 }}
          >
            {i18n.t("settings.brandKitIA.catalog.description")}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableCellHead}>
                  <strong>{i18n.t("settings.brandKitIA.catalog.colModel")}</strong>
                </TableCell>
                <TableCell className={classes.tableCellHead}>
                  <strong>{i18n.t("settings.brandKitIA.catalog.colType")}</strong>
                </TableCell>
                <TableCell align="center" className={classes.tableCellHead}>
                  <strong>{i18n.t("settings.brandKitIA.catalog.colYourPrice")}</strong>
                </TableCell>
                <TableCell align="center" className={classes.tableCellHead}>
                  <strong>{i18n.t("settings.brandKitIA.catalog.colOfficialPrice")}</strong>
                </TableCell>
                <TableCell align="center" className={classes.tableCellHead}>
                  <strong>{i18n.t("settings.brandKitIA.catalog.colSavings")}</strong>
                </TableCell>
                {user?.super && (
                  <TableCell
                    align="center"
                    className={classes.tableCellHead}
                    style={{ width: "150px", whiteSpace: "nowrap" }}
                  >
                    <strong>
                      {i18n.t("settings.brandKitIA.catalog.colProvider", "Proveedor")}
                    </strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {dbModels.map((model) => {
                const salePrice = Number(model.credits);
                const officialPrice = Number(model.officialPrice || 0);
                let savingsText = "-";
                let savingsColor = "inherit";

                if (officialPrice > 0 && officialPrice > salePrice) {
                  const percentage = (
                    ((officialPrice - salePrice) / officialPrice) *
                    100
                  ).toFixed(2);
                  savingsText = `🔥 ${percentage}% ${i18n.t(
                    "settings.brandKitIA.catalog.cheaper"
                  )}`;
                  savingsColor = "#00e676";
                }

                const apiId = (model.apiIdentifier || "").toLowerCase();
                const modelName = (model.name || "").toLowerCase();
                let provider = "Kie.ai";
                if (
                  apiId.includes("gpt") ||
                  apiId.includes("dall-e") ||
                  apiId.includes("tts-1") ||
                  apiId.includes("whisper") ||
                  apiId.includes("embedding")
                ) {
                  provider = "ChatGPT (OpenAI)";
                } else if (
                  apiId.includes("gemini") ||
                  modelName.includes("gemini")
                ) {
                  provider = "Google Gemini";
                } else if (
                  apiId.includes("claude") ||
                  modelName.includes("claude")
                ) {
                  provider = "Anthropic Claude";
                }

                return (
                  <TableRow key={model.id} hover>
                    <TableCell className={classes.tableCellBody}>{model.name}</TableCell>
                    <TableCell className={classes.tableCellBody}>{model.type}</TableCell>
                    <TableCell
                      align="center"
                      className={classes.tableCellBody}
                      style={{ fontWeight: "bold", color: theme.palette.primary.main }}
                    >
                      ${salePrice.toFixed(4)}
                    </TableCell>
                    <TableCell
                      align="center"
                      className={classes.tableCellBody}
                      style={{
                        textDecoration: "line-through",
                        color: "rgba(255, 255, 255, 0.4)",
                        fontSize: "0.95rem",
                      }}
                    >
                      {officialPrice > 0 ? `$${officialPrice.toFixed(4)}` : "-"}
                    </TableCell>
                    <TableCell
                      align="center"
                      className={classes.tableCellBody}
                      style={{ color: savingsColor, fontWeight: "bold" }}
                    >
                      {savingsText}
                    </TableCell>
                    {user?.super && (
                      <TableCell
                        align="center"
                        className={classes.tableCellBody}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {provider}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions style={{ borderTop: "1px solid rgba(255, 255, 255, 0.08)", padding: "12px 24px" }}>
          <Button
            onClick={() => setPricesModalOpen(false)}
            variant="outlined"
            className={classes.outlineButton}
          >
            {i18n.t("settings.brandKitIA.catalog.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BrandKitIA;
